// ╔══════════════════════════════════════════════════════════════════╗
// ║ ROUTES — o "dicionário" do app (Single Source of Truth).            ║
// ║ Nenhuma path é escrita à mão fora daqui. Componentes/botões/nav     ║
// ║ usam estes builders → o URL e a UI ficam sempre em sincronia.       ║
// ║ Navegação real via History API (createBrowserRouter).              ║
// ║                                                                    ║
// ║ IA de 3 tabs (produto convergido):                                 ║
// ║   • Eventos  — descoberta, bilhetes, carteira (raiz)               ║
// ║   • Tap      — networking/NFC/perfil/compatibilidade (portado)      ║
// ║   • Centro   — consola do organizador (role-aware, prefixo /descobrir) ║
// ║   • Hexis    — copilot flutuante (overlay deep-linkável)            ║
// ╚══════════════════════════════════════════════════════════════════╝

export const ROUTES = {
  org_onboarding: '/org/onboarding',
  user_onboarding: '/welcome',
  // Onboarding por PASSOS — cada passo é uma rota (deep-linkável, guarda progresso).
  onboardingSteps: ['/welcome', '/welcome/perfil', '/welcome/objetivos', '/welcome/pronto'] as const,

  // ── Tab 1 · EVENTOS (participante / descoberta) ──
  attendee: {
    explore: '/eventos',
    event: (slug: string) => `/eventos/${slug}`,
    tickets: '/bilhetes',
    wallet: '/carteira',
  },

  // ── Tab 2 · TAP (networking — portado da raiz, Fase 4) ──
  tap: {
    root: '/tap',
    user: (id: string) => `/tap/u/${id}`,
    profile: (id: string) => `/perfil/${id}`,
    connections: '/ligacoes',
  },

  // ── Perfil & conta (no Centro) ──
  conta: '/conta',
  perfil: '/conta/perfil',
  telegram: '/conta/telegram',
  definicoes: '/conta/definicoes',
  // ── Descobrir (no Centro) ──
  conteudos: '/conteudos',      // viewer de blog/notícias (todos)
  marketplace: '/marketplace',  // catálogo (todos)
  // Landing de categoria e página de produto — MESMA árvore, para o breadcrumb e
  // o tab ativo continuarem a funcionar sem casos especiais.
  marketCategory: (cat: string) => `/marketplace/c/${cat}`,
  product: (slug: string) => `/marketplace/${slug}`,

  // ── Tab 3 · CENTRO (hub role-aware) ──
  overview: '/descobrir',
  painel: '/descobrir/painel',   // dashboard/charts white-label (Decision Interface)
  // A visão de ecossistema (equipa) vive na consola HQ, fora do www.
  events: {
    root: '/descobrir/eventos',
    new: '/descobrir/eventos/novo',
    kpis: (id: string) => `/descobrir/eventos/${id}/kpis`,
    networking: (id: string) => `/descobrir/eventos/${id}/networking`,
  },
  tickets: {
    sales: '/descobrir/vendas',
    checkin: '/descobrir/checkin',
  },
  stand: '/descobrir/stand',
  // O Estúdio (CMS) vive na consola HQ (/conteudos) — fora do www.
  billing: {
    // Faturação do ORGANIZADOR (planos pro/enterprise) — protegida por permissão.
    plan: '/descobrir/billing/plano',
  },
  // Marcianus+ do LEITOR. Vive fora do /descobrir/billing porque não é a mesma
  // coisa nem é para a mesma pessoa: o PaywallGate mandava toda a gente para a
  // página do organizador, e um leitor normal batia num 403 ao carregar em «Assinar».
  plus: '/marcianus-plus',
  team: {
    members: '/descobrir/equipa/membros',
    branding: '/descobrir/equipa/marca',
  },

  // ── Overlays deep-linkáveis (Fase 5) ──
  hexis: '/hexis',
  notificacoes: '/notificacoes',
} as const;

// ── Tab-bar inferior (3 tabs · Tap ao centro como FAB). Data-driven. ──
export type TabIcon = 'eventos' | 'tap' | 'descobrir';
export interface TabDef {
  key: string;
  to: string;
  label: string;
  icon: TabIcon;
  eyebrow: string;       // sobrescrito no header conforme a tab (contexto)
  title: string;
  fab?: boolean;         // Tap = FAB central dourado
  match: string[];       // prefixos de path que ativam esta tab
}
export const TABS: TabDef[] = [
  { key: 'descobrir', to: ROUTES.overview, label: 'Descobrir', icon: 'descobrir',
    eyebrow: 'Descobre o que importa', title: 'Descobrir',
    match: ['/descobrir', '/conteudos', '/marketplace'] },
  { key: 'tap', to: ROUTES.tap.root, label: 'Tap', icon: 'tap', fab: true,
    eyebrow: 'Conhece alguém em segundos', title: 'Conectar',
    match: ['/tap', '/perfil', '/ligacoes'] },
  { key: 'eventos', to: ROUTES.attendee.explore, label: 'Eventos', icon: 'eventos',
    eyebrow: 'Escolhe o teu próximo evento', title: 'Eventos',
    match: ['/eventos'] },
];

/** Devolve a tab ativa para um pathname (para o realce e o header). */
export function activeTabFor(pathname: string): string | null {
  const hit = TABS.find((t) => t.match.some((p) => pathname === p || pathname.startsWith(p + '/')));
  return hit?.key ?? null;
}
export function tabDefFor(pathname: string): TabDef | undefined {
  const key = activeTabFor(pathname);
  return TABS.find((t) => t.key === key);
}

// ── Header por ROTA — o cabeçalho reflete a tela real (não só a tab). ──
// REGRA (um header, sempre): rotas listadas em ROUTE_HEADER são nomeadas pelo
// app-header → o corpo NÃO repete título (só action-rows). Subtelas de /descobrir
// mostram a tab no app-header + PageHeader próprio no corpo (tab ≠ página).
export interface HeaderInfo { eyebrow: string; title: string; }
const ROUTE_HEADER: Record<string, HeaderInfo> = {
  '/conta': { eyebrow: 'A tua conta Marcianus', title: 'A minha conta' },
  '/conta/perfil': { eyebrow: 'Identidade e definições', title: 'Perfil' },
  '/conta/telegram': { eyebrow: 'Hexis no Telegram', title: 'Telegram' },
  '/conta/definicoes': { eyebrow: 'Permissões e privacidade', title: 'Definições' },
  '/bilhetes': { eyebrow: 'Acesso e comprovativos', title: 'Os meus bilhetes' },
  '/carteira': { eyebrow: 'Saldo MXC e movimentos', title: 'Carteira' },
  '/conteudos': { eyebrow: 'Blog · Notícias · Relatórios', title: 'Conteúdos' },
  '/marketplace': { eyebrow: 'Produtos e serviços', title: 'Marketplace' },
  '/marcianus-plus': { eyebrow: 'Subscrição', title: 'Marcianus+' },
  '/hexis': { eyebrow: 'O teu copiloto de networking', title: 'Hexis AI' },
  '/ligacoes': { eyebrow: 'A tua rede', title: 'Ligações' },
  '/notificacoes': { eyebrow: 'Tempo real', title: 'Notificações' },
};
const DYNAMIC_HEADER: { re: RegExp; header: HeaderInfo }[] = [
  { re: /^\/eventos\/[^/]+$/, header: { eyebrow: 'Detalhe do evento', title: 'Evento' } },
  { re: /^\/conteudos\/[^/]+$/, header: { eyebrow: 'Leitura', title: 'Conteúdo' } },
  { re: /^\/(perfil|tap\/u)\/[^/]+$/, header: { eyebrow: 'Perfil', title: 'Perfil' } },
  // A categoria tem de vir ANTES do produto: /marketplace/c/saas casa nas duas
  // expressões, e a primeira a casar ganha. Invertê-las dava a um ecrã de
  // categoria o cabeçalho de um produto.
  { re: /^\/marketplace\/c\/[^/]+$/, header: { eyebrow: 'Marketplace', title: 'Categoria' } },
  { re: /^\/marketplace\/[^/]+$/, header: { eyebrow: 'Produtos e serviços', title: 'Produto' } },
];

/** Cabeçalho (eyebrow+título) para um pathname: rota própria > detalhe > tab. */
export function headerFor(pathname: string): HeaderInfo {
  if (ROUTE_HEADER[pathname]) return ROUTE_HEADER[pathname];
  const dyn = DYNAMIC_HEADER.find((d) => d.re.test(pathname));
  if (dyn) return dyn.header;
  const t = tabDefFor(pathname);
  return t ? { eyebrow: t.eyebrow, title: t.title } : { eyebrow: '', title: 'Marcianus' };
}

